/* eslint-disable @typescript-eslint/no-explicit-any */
//This is a temporary file that includes the multiple selection in the dropdown
//This integration to the main dropdown will be retaken in another ticket.

import GenericBadge, { BadgeProps } from "@ui-components/Badge/GenericBadge";
import GenericButton, { GenericButtonProps } from "@ui-components/Button/GenericButton";
import SegmentedControl, { SegmentedControlProps } from "@ui-components/SegmentedContol/GenericSegmentedContol";
import React, { useEffect, useRef, useState } from "react";


export type DropDownSize = "sm" | "md" | "lg";

const sizeClasses: Record<DropDownSize, string> = {
  sm: "h-8 px-2 gap-1 text-sm",
  md: "py-1.5 px-3 gap-2 text-sm",
  lg: "h-12 px-4 gap-2 text-base",
};

export interface GenericMultiDropdownProps {
  items: string[];
  defaultLabel?: string;
  size?: DropDownSize;
  widthClass?: string;
  disabled?: boolean;
  /**
   * Callback invoked with an array of selected items.
   */
  onSelect?: (selectedItems: string[]) => void;
  /**
   * When used as a controlled component, value should be an array of strings.
   */
  value?: string[];
  className?: string;
  icon?: React.ReactNode;
  fetchOptions?: (selectedItem: string) => void;
  position?: "left" | "right";
  minWidthClass?: string;
  maxWidthClass?: string;
  dropdownMaxWidthClass?: string;
  dropdownSubtitle?: string;
  emptyItemsMessage?: string;
  emptyIcon?: React.ReactNode;
  labelKey?: string;
  badgeProps?: Omit<BadgeProps, "children">;
  tabs?: SegmentedControlProps; 
  renderItem?: (item: any) => React.ReactNode;
  buttonProps?: GenericButtonProps;
}

const GenericMultiDropdown: React.FC<GenericMultiDropdownProps> = ({
  items,
  defaultLabel = "Select a value",
  size = "md",
  widthClass = "w-full",
  disabled = false,
  onSelect,
  value,
  className = "",
  icon,
  fetchOptions,
  position = "left",
  minWidthClass = "min-w-[140px]",
  maxWidthClass = "max-w-[200px]",
  dropdownMaxWidthClass,
  dropdownSubtitle,
  emptyItemsMessage,
  emptyIcon,
  labelKey = "nameDisplay",
  badgeProps,
  tabs,
  renderItem,
  buttonProps,
}) => {
  // Use an array for multi-select mode.
  const [internalSelected, setInternalSelected] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<string | number>(tabs?.value ?? "0");

  // Helper to extract the label from an item.
  const getItemLabel = (item: any): string => {
    if (!item) return "";
    if (typeof item === "object") {
      return item[labelKey] ? String(item[labelKey]) : String(item);
    }
    return String(item);
  };

  // Determine if we're in "icon-only" mode.
  const iconOnlyMode = Boolean(icon && defaultLabel === "");

  // Normalize the selected items: if controlled (value is provided), use it; otherwise, use internal state.
  const selectedItems = value !== undefined ? value : internalSelected;

  // Compute the displayed value as a comma-separated string of selected items.
  const displayedValue =
    items.length === 0
      ? "No options available"
      : selectedItems.length === 0
      ? defaultLabel
      : selectedItems.map(getItemLabel).join(", ");

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside.
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  };

  // Handle selection toggling.
  const handleSelect = async (option: string) => {
    let updatedSelected: string[];
    if (selectedItems.includes(option)) {
      // Remove the option if it's already selected
      updatedSelected = selectedItems.filter((item) => item !== option);
    } else {
      // Add the option if it's not selected
      updatedSelected = [...selectedItems, option];
    }
    if (value === undefined) {
      setInternalSelected(updatedSelected);
    }
    if (fetchOptions) {
      await fetchOptions(option);
    }
    onSelect && onSelect(updatedSelected);
  };
  

  // Classes for width handling.
  const buttonWidthClass = iconOnlyMode ? "w-auto" : `${widthClass} ${minWidthClass} ${maxWidthClass}`;
  const containerWidthClass = iconOnlyMode ? "w-200" : widthClass;
  const containerMaxWidth = iconOnlyMode && dropdownMaxWidthClass ? dropdownMaxWidthClass : maxWidthClass;
  const triggerButtonSizeClass = sizeClasses[size];

  // Render trigger content.
  const triggerContent = (
    <div className="flex items-center justify-between w-full">
      {!iconOnlyMode && displayedValue !== "" && <span>{displayedValue}</span>}
      {icon || (
        <svg className="h-4 w-4 ml-2 text-semantic-text-secondary" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M10 12a.997.997 0 01-.707-.293l-4-4
               a1 1 0 111.414-1.414L10 9.586
               l3.293-3.293a1 1 0 011.414
               1.414l-4 4A.997.997 0 0110
               12z"
            clipRule="evenodd"
          />
        </svg>
      )}
    </div>
  );

  // Render the trigger button.
  const triggerButton = buttonProps ? (
    <div className={`${buttonWidthClass}`}>
      <GenericButton
        {...buttonProps}
        onClick={toggleDropdown}
        disabled={disabled}
        style={{ width: "100%", ...(buttonProps.style || {}) }}
      >
        {triggerContent}
      </GenericButton>
    </div>
  ) : (
    <button
      onClick={toggleDropdown}
      disabled={disabled}
      className={`
        ${buttonWidthClass} ${triggerButtonSizeClass}
        text-semantic-text-secondary bg-semantic-bg-primary-alt
        border border-semantic-border-secondary rounded-sm
        hover:bg-semantic-state-active transition-colors
        ${disabled ? "opacity-50 cursor-not-allowed" : ""}
      `}
    >
      {triggerContent}
    </button>
  );

  // Optionally wrap with a badge.
  const triggerWithBadge = badgeProps ? (
    <GenericBadge {...badgeProps}>{triggerButton}</GenericBadge>
  ) : (
    triggerButton
  );

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {triggerWithBadge}
      {isOpen && (
        <div
          className={`
            absolute mt-1
            ${containerWidthClass} ${minWidthClass} ${containerMaxWidth}
            bg-semantic-bg-primary-alt
            border border-semantic-border-secondary
            rounded-sm
            shadow-md
            z-50
            ${position === "left" ? "left-0" : "right-0"}
          `}
        >
          {iconOnlyMode && dropdownSubtitle && (
            <div className="px-3 py-2 border-b border-semantic-border-secondary">
              <span className="text-sm font-medium text-semantic-text-secondary">
                {dropdownSubtitle}
              </span>
            </div>
          )}
          {tabs && (
            <SegmentedControl
              {...tabs}
              value={selectedTab}
              onChange={(val) => setSelectedTab(val)}
              className="p-2"
              maxWidthClass="full"
            />
          )}
          <ul className="py-1">
            {items.length === 0 ? (
              <li>
                <div className="flex items-center justify-center px-3 py-2 text-sm text-semantic-text-secondary cursor-default">
                  {emptyIcon && <span className="mr-1">{emptyIcon}</span>}
                  <span>{emptyItemsMessage || "No options available"}</span>
                </div>
              </li>
            ) : (
              items.map((item) => {
                const label = getItemLabel(item);
                const isSelected = selectedItems.includes(label);
                return (
                  <li key={label}>
                    {renderItem ? (
                      renderItem(item)
                    ) : (
                      <button
                        onClick={() => handleSelect(label)}
                        className={`
                          w-full text-left
                          px-3 py-2
                          text-sm
                          text-semantic-text-secondary
                          hover:bg-semantic-state-active
                          hover:text-semantic-text-brand
                          transition-colors
                          ${isSelected ? "bg-semantic-state-selected" : ""}
                        `}
                      >
                        {label}
                      </button>
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default GenericMultiDropdown;
